import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, Column, DistributiveOmit, StyleProps } from "@hex-insights/core";
import { FieldIf, FieldSet, Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import {
	StudentBehaviorNoteCreateMutation,
	StudentBehaviorNoteDetailQuery,
	StudentBehaviorNoteUpdateMutation,
} from "../../../Utilities";
import { StudentBehaviorNoteFormat } from "../../Format";
import { StudentBehaviorNoteFormState, StudentBehaviorNoteFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<StudentBehaviorNoteCreateMutation["createStudentBehaviorNote"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentBehaviorNoteFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentBehaviorNote model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentBehaviorNoteFormValues.Create>;
	create: CreateFunction<StudentBehaviorNoteFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentBehaviorNoteFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentBehaviorNoteFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentBehaviorNote model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentBehaviorNote.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<StudentBehaviorNoteFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<Field.IsIncidentPhysicalContactAggression formState={formState} />
			<Field.IsIncidentFightingPhysicalAggression formState={formState} />
			<Field.IsIncidentVerbalAbuseDisrespectingStudent formState={formState} />
			<Field.IsIncidentDisrepectingTeacher formState={formState} />
			<Field.IsIncidentDisruption formState={formState} />
			<Field.IsIncidentDefianceOrNonCompliance formState={formState} />
			<Field.IsIncidentOther formState={formState} />
			<Field.IncidentOther formState={formState} />
			<Field.IsMotivationAttentionSeeking formState={formState} />
			<Field.IsMotivationReaction formState={formState} />
			<Field.IsMotivationAnger formState={formState} />
			<Field.IsMotivationFrustration formState={formState} />
			<Field.IsMotivationHabit formState={formState} />
			<Field.IsMotivationLackOfSelfContol formState={formState} />
			<Field.IsMotivationOther formState={formState} />
			<Field.MotivationOther formState={formState} />
			<Field.IsConsequenceRecDirectorIntervention formState={formState} />
			<Field.IsConsequenceRecSenOrSelIntervention formState={formState} />
			<Field.IsConsequenceRecStudentServiceTeamIntervention formState={formState} />
			<Field.IsConsequenceRecParentContactFromReporter formState={formState} />
			<Field.IsConsequenceRecParentContactFromSchoolDirector formState={formState} />
			<Field.IsConsequenceRecOther formState={formState} />
			<Field.ConsequenceRecOther formState={formState} />
			<Field.AdditionalComments formState={formState} />
			<Field.StudentNote.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentBehaviorNoteUpdateMutation["updateStudentBehaviorNote"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentBehaviorNoteFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentBehaviorNote model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({
		...formStateArgs,
		studentBehaviorNote: props.studentBehaviorNote,
	});

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentBehaviorNote: StudentBehaviorNoteDetailQuery["studentBehaviorNote"];
	formState: FormState<StudentBehaviorNoteFormValues.Detail>;
	update: UpdateFunction<StudentBehaviorNoteFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentBehaviorNoteFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentBehaviorNoteFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentBehaviorNote model using the given form state.
 */
export function Detail<T>({
	formState,
	studentBehaviorNote,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName =
		`studentBehaviorNote.detail.${studentBehaviorNote.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentBehaviorNote={studentBehaviorNote} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<StudentBehaviorNoteFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the StudentBehaviorNote model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, studentBehaviorNote: props.studentBehaviorNote });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentBehaviorNote: StudentBehaviorNoteDetailQuery["studentBehaviorNote"];
	formState: FormState<StudentBehaviorNoteFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentBehaviorNote model using the given form state.
 */
export function ReadOnly({ formState, studentBehaviorNote, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentBehaviorNote={studentBehaviorNote} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentBehaviorNote: StudentBehaviorNoteDetailQuery["studentBehaviorNote"];
	formState: FormState<StudentBehaviorNoteFormValues.Detail>;
};

function DetailFields({ formState, studentBehaviorNote }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<Field.IsIncidentPhysicalContactAggression formState={formState} />
			<Field.IsIncidentFightingPhysicalAggression formState={formState} />
			<Field.IsIncidentVerbalAbuseDisrespectingStudent formState={formState} />
			<Field.IsIncidentDisrepectingTeacher formState={formState} />
			<Field.IsIncidentDisruption formState={formState} />
			<Field.IsIncidentDefianceOrNonCompliance formState={formState} />
			<Field.IsIncidentOther formState={formState} />
			<Field.IncidentOther formState={formState} />
			<Field.IsMotivationAttentionSeeking formState={formState} />
			<Field.IsMotivationReaction formState={formState} />
			<Field.IsMotivationAnger formState={formState} />
			<Field.IsMotivationFrustration formState={formState} />
			<Field.IsMotivationHabit formState={formState} />
			<Field.IsMotivationLackOfSelfContol formState={formState} />
			<Field.IsMotivationOther formState={formState} />
			<Field.MotivationOther formState={formState} />
			<Field.IsConsequenceRecDirectorIntervention formState={formState} />
			<Field.IsConsequenceRecSenOrSelIntervention formState={formState} />
			<Field.IsConsequenceRecStudentServiceTeamIntervention formState={formState} />
			<Field.IsConsequenceRecParentContactFromReporter formState={formState} />
			<Field.IsConsequenceRecParentContactFromSchoolDirector formState={formState} />
			<Field.IsConsequenceRecOther formState={formState} />
			<Field.ConsequenceRecOther formState={formState} />
			<Field.AdditionalComments formState={formState} />
			<Field.StudentNote.Select formState={formState} currentInstance={studentBehaviorNote.studentNote} />
		</React.Fragment>
	);
}

const incidentTypeFieldNames: (keyof Omit<StudentBehaviorNoteFormValues.CreateInStudentNote, "studentNoteID">)[] = [
	"isIncidentPhysicalContactAggression",
	"isIncidentFightingPhysicalAggression",
	"isIncidentVerbalAbuseDisrespectingStudent",
	"isIncidentDisrepectingTeacher",
	"isIncidentDisruption",
	"isIncidentDefianceOrNonCompliance",
	"isIncidentOther",
	"incidentOther",
];

const motivationFieldNames: (keyof Omit<StudentBehaviorNoteFormValues.CreateInStudentNote, "studentNoteID">)[] = [
	"isMotivationAttentionSeeking",
	"isMotivationReaction",
	"isMotivationAnger",
	"isMotivationFrustration",
	"isMotivationHabit",
	"isMotivationLackOfSelfContol",
	"isMotivationOther",
	"motivationOther",
];

const consequenceRecFieldNames: (keyof Omit<StudentBehaviorNoteFormValues.CreateInStudentNote, "studentNoteID">)[] = [
	"isConsequenceRecDirectorIntervention",
	"isConsequenceRecSenOrSelIntervention",
	"isConsequenceRecStudentServiceTeamIntervention",
	"isConsequenceRecParentContactFromReporter",
	"isConsequenceRecParentContactFromSchoolDirector",
	"isConsequenceRecOther",
	"consequenceRecOther",
];

export type CreateFieldsInStudentNoteProps = {
	formState: FormState<StudentBehaviorNoteFormValues.CreateInStudentNote>;
};

export function CreateFieldsInStudentNote({ formState }: CreateFieldsInStudentNoteProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<FieldSet
				formState={formState}
				names={incidentTypeFieldNames}
				label={StudentBehaviorNoteFormat.fieldSetLabels.incidentType}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsIncidentPhysicalContactAggression formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentFightingPhysicalAggression formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentVerbalAbuseDisrespectingStudent formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentDisrepectingTeacher formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentDisruption formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentDefianceOrNonCompliance formState={formState} style={{ width: "100%" }} />
					<Field.IsIncidentOther formState={formState} style={{ width: "100%" }} />
					<FieldIf formState={formState} name="incidentOther" condition={formState.formValues.isIncidentOther}>
						<Field.IncidentOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
			<FieldSet
				formState={formState}
				names={motivationFieldNames}
				label={StudentBehaviorNoteFormat.fieldSetLabels.motivation}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsMotivationAttentionSeeking formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationReaction formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationAnger formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationFrustration formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationHabit formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationLackOfSelfContol formState={formState} style={{ width: "100%" }} />
					<Field.IsMotivationOther formState={formState} style={{ width: "100%" }} />
					<FieldIf formState={formState} name="motivationOther" condition={formState.formValues.isMotivationOther}>
						<Field.MotivationOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
			<FieldSet
				formState={formState}
				names={consequenceRecFieldNames}
				label={StudentBehaviorNoteFormat.fieldSetLabels.consequenceRec}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsConsequenceRecDirectorIntervention formState={formState} style={{ width: "100%" }} />
					<Field.IsConsequenceRecSenOrSelIntervention formState={formState} style={{ width: "100%" }} />
					<Field.IsConsequenceRecStudentServiceTeamIntervention formState={formState} style={{ width: "100%" }} />
					<Field.IsConsequenceRecParentContactFromReporter formState={formState} style={{ width: "100%" }} />
					<Field.IsConsequenceRecParentContactFromSchoolDirector formState={formState} style={{ width: "100%" }} />
					<Field.IsConsequenceRecOther formState={formState} style={{ width: "100%" }} />
					<FieldIf
						formState={formState}
						name="consequenceRecOther"
						condition={formState.formValues.isConsequenceRecOther}
					>
						<Field.ConsequenceRecOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
			<Field.AdditionalComments formState={formState} style={{ width: "100%" }} />
		</React.Fragment>
	);
}
